export type BoardMcpEndpointResult =
  | { ok: true; endpoint: string }
  | { ok: false; message: string };

export interface BoardMcpClientRecipe {
  id: "codex" | "claude-code" | "opencode";
  name: string;
  qualifiedVersion: string;
  configurationLanguage: string;
  setupSnippet: string;
  connectionCheck: string;
  localClearCommand: string;
  browserAuthorization: string;
  restartAndRefresh: string;
  callbackGuidance: string;
  troubleshooting: readonly string[];
}

export function resolveBoardMcpEndpoint(boardApiUrl: string): BoardMcpEndpointResult {
  const configuredUrl = boardApiUrl.trim();

  if (!configuredUrl) {
    return {
      ok: false,
      message: "BOARD_API_URL is required to build the Board MCP endpoint.",
    };
  }

  try {
    const baseUrl = new URL(configuredUrl);
    if (baseUrl.protocol !== "http:" && baseUrl.protocol !== "https:") {
      throw new TypeError("Unsupported BOARD_API_URL protocol");
    }

    baseUrl.pathname = `${baseUrl.pathname.replace(/\/+$/, "")}/mcp`;
    baseUrl.search = "";
    baseUrl.hash = "";
    return { ok: true, endpoint: baseUrl.toString() };
  } catch {
    return {
      ok: false,
      message: "BOARD_API_URL must be a valid HTTP or HTTPS URL.",
    };
  }
}

export const boardMcpCapabilities = [
  "Diagrams: list, search, read, create, rename, share, and restore.",
  "Nodes: add, update, and delete.",
  "Edges: add and delete.",
  "Comments: add comments to diagrams you can access.",
  "Resources: visible diagrams and teams, plus diagram metadata, snapshots, and versions.",
] as const;

export const boardMcpPermissions = [
  "Viewer can read a diagram.",
  "Commenter can read and add comments.",
  "Editor can read, comment, mutate, and restore versions.",
  "Owner can do all Editor actions and share the diagram, as can admins of a team on its share list at Editor.",
  "Creating a diagram makes you its owner.",
] as const;

export function buildBoardMcpClientRecipes(endpoint: string): readonly BoardMcpClientRecipe[] {
  return [
    {
      id: "codex",
      name: "Codex CLI",
      qualifiedVersion: "0.154.0",
      configurationLanguage: "TOML and shell",
      setupSnippet: `# In the selected profile's $CODEX_HOME/config.toml, outside every server table:
mcp_oauth_credentials_store = "file"

# Then configure and authorize Board:
codex mcp add board --url ${endpoint} --oauth-client-id board-mcp-codex
# The add command starts an automatic OAuth handoff that requests unsupported openid.
# Cancel it or let it finish with invalid_scope, then run the qualified login:
codex mcp login board --scopes board:mcp,offline_access`,
      connectionCheck: "codex mcp get board",
      localClearCommand: "codex mcp logout board\ncodex mcp remove board",
      browserAuthorization:
        "The explicit login opens Trillion Passport in your browser. Approve the requested Board access; do not enter a client secret or copy a browser token.",
      restartAndRefresh:
        "After authorization, fully restart Codex and run a normal Board call. The file-backed profile should reuse the connection and refresh automatically after access expires.",
      callbackGuidance:
        "Codex automatically allocates 127.0.0.1:{port}/callback. If a handoff expires or leaves a stale listener, cancel it and rerun the explicit login instead of selecting a port.",
      troubleshooting: [
        "Do not add --oauth-resource. Codex already sends the resource discovered from Board metadata.",
        "An invalid_scope result from the add command's automatic openid handoff is expected; the explicit scoped login is the supported path.",
        "Use a separate CODEX_HOME with the file credential store for each independent profile.",
      ],
    },
    {
      id: "claude-code",
      name: "Claude Code",
      qualifiedVersion: "2.1.267",
      configurationLanguage: "Shell",
      setupSnippet: `claude mcp add --transport http --scope user --client-id board-mcp-claude-code board ${endpoint}
claude mcp login board`,
      connectionCheck: "claude mcp get board\n# In Claude Code, /mcp also shows connection and discovery details.",
      localClearCommand: "claude mcp logout board\nclaude mcp remove board -s user",
      browserAuthorization:
        "Login opens Trillion Passport in your browser. Approve the Board connection; no client secret or copied browser token is used.",
      restartAndRefresh:
        "Fully exit and restart Claude Code, then make a normal Board call. The user-scoped connection should be reused and refresh automatically after access expires.",
      callbackGuidance:
        "Claude Code normally allocates localhost:{port}/callback. If you need a known free port, add --callback-port <port> to the server-add command; it is not a login flag.",
      troubleshooting: [
        "If the browser returns after the listener has expired, rerun claude mcp login board.",
        "For simultaneous profiles, use separate CLAUDE_CONFIG_DIR and CLAUDE_SECURESTORAGE_CONFIG_DIR values.",
        "Use /mcp to inspect authentication and tool discovery before retrying setup.",
      ],
    },
    {
      id: "opencode",
      name: "OpenCode",
      qualifiedVersion: "1.18.29 (V1)",
      configurationLanguage: "JSON and shell",
      setupSnippet: `{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "board": {
      "type": "remote",
      "url": "${endpoint}",
      "oauth": {
        "clientId": "board-mcp-opencode",
        "scope": "board:mcp offline_access"
      }
    }
  }
}

opencode mcp auth board`,
      connectionCheck: "opencode mcp list\nopencode mcp debug board",
      localClearCommand: "opencode mcp logout board",
      browserAuthorization:
        "The auth command opens Trillion Passport in your browser. Approve the Board connection without entering a client secret or copying a browser token.",
      restartAndRefresh:
        "Fully exit and restart OpenCode, then make a normal Board call. The isolated profile should reuse its credential and refresh automatically after access expires.",
      callbackGuidance:
        "OpenCode 1.18.29 uses the fixed callback http://127.0.0.1:19876/mcp/oauth/callback. If that port is busy, stop the process using it and retry; no callback-port override is qualified.",
      troubleshooting: [
        "Keep the V1 top-level mcp object and camelCase oauth.clientId. Do not substitute a V2 or snake_case shape.",
        "Use separate XDG_CONFIG_HOME, XDG_DATA_HOME, XDG_CACHE_HOME, and XDG_STATE_HOME values for independent profiles.",
        "Run the debug command to distinguish configuration, authentication, and connection failures.",
      ],
    },
  ];
}
